import React, { useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { typography } from '../../../shared/theme/typography';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Fila sutil bajo el CTA: qué cuenta de Mercado Pago va a pagar (payer_email
 * del checkout — MP valida que quien paga use esa cuenta). El dueño puede
 * cambiarla si va a pagar con una MP de otro e-mail; tras confirmar, se le
 * indica el siguiente paso (entrar a MP con esa cuenta).
 */
export function PayerEmailRow({
  email,
  defaultEmail,
  onChange,
}: {
  /** e-mail pagador efectivo (override o Vuelto). */
  email: string;
  /** e-mail Vuelto del usuario — para "volver a mi e-mail Vuelto". */
  defaultEmail: string;
  /** Recibe el nuevo override, o null para volver al e-mail Vuelto. */
  onChange: (next: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const overridden = !!email && email !== defaultEmail;

  const startEdit = () => {
    setDraft(email);
    setError(null);
    setEditing(true);
  };

  const confirm = () => {
    const next = draft.trim().toLowerCase();
    if (!EMAIL_RE.test(next)) {
      setError('Ingresá un e-mail válido.');
      return;
    }
    setError(null);
    setEditing(false);
    onChange(next === defaultEmail ? null : next);
  };

  if (editing) {
    return (
      <View style={styles.editBlock}>
        <Text style={styles.text}>E-mail de la cuenta de Mercado Pago que va a pagar:</Text>
        <TextInput
          style={styles.input}
          value={draft}
          onChangeText={(v) => {
            setDraft(v);
            if (error) setError(null);
          }}
          placeholder="nombre@mail.com"
          placeholderTextColor={ticketColors.ink3}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          onSubmitEditing={confirm}
        />
        {error ? <Text style={styles.error}>{error}</Text> : null}
        <Text style={styles.text}>
          Mercado Pago solo deja pagar con la cuenta de ese e-mail — si tu MP usa otro correo,
          escribilo acá.
        </Text>
        <View style={styles.actions}>
          <TouchableOpacity onPress={confirm} hitSlop={8} accessibilityRole="button">
            <Text style={styles.link}>Confirmar</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setEditing(false)} hitSlop={8} accessibilityRole="button">
            <Text style={styles.cancel}>Cancelar</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (overridden) {
    return (
      <View style={styles.editBlock}>
        <Text style={styles.text}>
          Vas a pagar con la cuenta MP de <Text style={styles.strong}>{email}</Text>
        </Text>
        <Text style={styles.text}>
          Ahora seguí con el pago y entrá a Mercado Pago con esa cuenta.
        </Text>
        <View style={styles.actions}>
          <TouchableOpacity onPress={startEdit} hitSlop={8} accessibilityRole="button">
            <Text style={styles.link}>cambiar</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => onChange(null)} hitSlop={8} accessibilityRole="button">
            <Text style={styles.cancel}>volver a {defaultEmail || 'mi e-mail'}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.row}>
      <Text style={styles.text}>
        Pagás con la cuenta MP de <Text style={styles.strong}>{email || 'tu e-mail'}</Text>
      </Text>
      <TouchableOpacity onPress={startEdit} hitSlop={8} accessibilityRole="button">
        <Text style={styles.link}>usar otra</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  text: {
    ...typography.caption,
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    lineHeight: 16,
    color: ticketColors.ink3,
    textAlign: 'center',
  },
  strong: { fontWeight: '600' },
  link: {
    ...typography.caption,
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.brandInk,
    textDecorationLine: 'underline',
  },
  cancel: {
    ...typography.caption,
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    color: ticketColors.ink3,
  },
  editBlock: { gap: spacing.xs, alignSelf: 'stretch', alignItems: 'center' },
  input: {
    ...typography.body,
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    alignSelf: 'stretch',
  },
  error: {
    ...typography.caption,
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    color: ticketColors.bad,
  },
  actions: { flexDirection: 'row', gap: spacing.md },
});
